import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Card, Button, Spinner, PageHeader, StatCard } from '../components/common';
import { formatDataOra } from '../utils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

const progAPI = {
  getGhisee:   ()         => api.get('/programari/ghisee').then(r => r.data.data),
  getCalendar: (id, data) => api.get('/programari/calendar', { params: { ghiseuId: id, dataStart: data } }).then(r => r.data.data),
  getSloturi:  (id, data) => api.get('/programari/sloturi',  { params: { ghiseuId: id, data } }).then(r => r.data.data),
  creeaza:     (d)        => api.post('/programari', d),
  getMele:     ()         => api.get('/programari/mele').then(r => r.data.data),
  getAzi:      (id)       => api.get('/programari/azi', { params: id ? { ghiseuId: id } : {} }).then(r => r.data),
  updateStatus:(id, d)    => api.patch(`/programari/${id}/status`, d),
  anuleaza:    (id, m)    => api.delete(`/programari/${id}`, { data: { motiv: m } }),
};

const STATUS_PROG = {
  confirmata:    { label: 'Confirmată',    color: '#059669', bg: '#d1fae5' },
  prezenta:      { label: 'Prezentă',      color: '#2563eb', bg: '#dbeafe' },
  neprezentata:  { label: 'Neprezentată',  color: '#dc2626', bg: '#fee2e2' },
  anulata:       { label: 'Anulată',       color: '#6b7280', bg: '#f3f4f6' },
  reprogramata:  { label: 'Reprogramată',  color: '#7c3aed', bg: '#ede9fe' },
};

export default function ProgramariPage() {
  const { isFunctionar } = useAuth();
  const [tab, setTab]    = useState('programeaza');

  return (
    <Layout>
      <PageHeader title="Programări ghișeu" subtitle="Rezervă un slot la primărie fără să aștepți la coadă" />

      <div style={tabs}>
        {[
          ['programeaza', 'Fă o programare'],
          ['ale-mele',    'Programările mele'],
          ...(isFunctionar ? [['agenda', 'Agenda azi']] : []),
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...tabBtn, ...(tab === key ? tabActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'programeaza' && <TabProgrameaza />}
      {tab === 'ale-mele'    && <TabAleMele />}
      {tab === 'agenda'      && isFunctionar && <TabAgenda />}
    </Layout>
  );
}

// ── Tab: Fă o programare ──────────────────────────────────────
function TabProgrameaza() {
  const { user } = useAuth();
  const qc       = useQueryClient();
  const w = useWindowWidth();
  const isMobile = w < 640;
  const [pas, setPas]             = useState(1);
  const [ghiseuId, setGhiseuId]   = useState('');
  const [dataAleasa, setData]     = useState('');
  const [slotAles, setSlot]       = useState(null);
  const [confirmat, setConfirmat] = useState(null);

  const { register, handleSubmit, formState: { errors } } = useForm();

  const { data: ghisee, isLoading: lghi } = useQuery('ghisee', progAPI.getGhisee);

  const dataStart = new Date().toISOString().split('T')[0];
  const { data: calendar } = useQuery(
    ['calendar', ghiseuId, dataStart],
    () => progAPI.getCalendar(ghiseuId, dataStart),
    { enabled: !!ghiseuId }
  );

  const { data: sloturi, isLoading: lslo } = useQuery(
    ['sloturi', ghiseuId, dataAleasa],
    () => progAPI.getSloturi(ghiseuId, dataAleasa),
    { enabled: !!ghiseuId && !!dataAleasa }
  );

  const mutation = useMutation(progAPI.creeaza, {
    onSuccess: (r) => {
      setConfirmat(r.data.programare);
      qc.invalidateQueries('programari-mele');
      setPas(4);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Eroare programare'),
  });

  function onSubmit(formData) {
    mutation.mutate({
      ghiseuId,
      data:          dataAleasa,
      oraStart:      slotAles.oraStart,
      cetateanNume:  formData.cetateanNume,
      cetateanEmail: formData.cetateanEmail,
      cetateanPhone: formData.cetateanPhone,
      motiv:         formData.motiv,
    });
  }

  if (lghi) return <Spinner />;

  // Pas 4: Confirmare
  if (pas === 4 && confirmat) {
    const ghiseu = ghisee?.find(g => g.id === ghiseuId);
    return (
      <Card>
        <div style={succesBox}>
          <div style={{ fontSize: '56px' }}>✅</div>
          <h2 style={{ color: '#059669', margin: '16px 0 8px' }}>Programare confirmată!</h2>
          <div style={codBox}>{confirmat.codConfirmare}</div>
          <p style={{ color: '#555', margin: '8px 0' }}>Salvați sau fotografiați codul de confirmare</p>
          <div style={detaliiBox}>
            {[
              ['📅 Data', new Date(confirmat.data).toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })],
              ['🕐 Ora',  `${confirmat.oraStart} – ${confirmat.oraSfarsit}`],
              ['📍 Ghișeu', ghiseu?.denumire || ''],
              ['🏢 Locație', ghiseu?.locatie || ''],
            ].map(([k, v]) => (
              <div key={k} style={detaliiRow}>
                <span style={{ color: '#888' }}>{k}</span>
                <span style={{ fontWeight: '600' }}>{v}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '13px', color: '#888', marginTop: '16px' }}>
            Veți primi confirmarea pe email și SMS. Prezentați codul la ghișeu.
          </p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'center' }}>
            <Button onClick={() => { setPas(1); setGhiseuId(''); setData(''); setSlot(null); setConfirmat(null); }}>
              Nouă programare
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* Progress bar */}
      <div style={progresSteps}>
        {['Ghișeu','Dată și oră','Date personale','Confirmat'].map((step, i) => (
          <div key={step} style={stepItem}>
            <div style={{ ...stepCircle, ...(pas > i ? stepDone : pas === i + 1 ? stepActive : {}) }}>
              {pas > i ? '✓' : i + 1}
            </div>
            <span style={{ ...stepLabel, color: pas >= i + 1 ? '#2d2d7e' : '#9ca3af' }}>{step}</span>
          </div>
        ))}
      </div>

      {/* Pas 1: Selectare ghișeu */}
      {pas === 1 && (
        <Card title="Selectează ghișeul">
          <div style={ghiseeGrid}>
            {(ghisee || []).map(g => (
              <button key={g.id} onClick={() => { setGhiseuId(g.id); setPas(2); }}
                style={ghiseuBtn}>
                <span style={{ fontSize: '28px' }}>🏛</span>
                <strong style={{ fontSize: '14px' }}>{g.denumire}</strong>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{g.locatie}</span>
                <span style={{ fontSize: '12px', color: '#2d2d7e' }}>
                  {g.programari_azi} programări azi
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>{g.departament_nume}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Pas 2: Selectare dată și oră */}
      {pas === 2 && (
        <Card title="Selectează data și ora">
          {/* Mini calendar 7 zile */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>
              Selectează o zi disponibilă (afișăm următoarele 7 zile):
            </p>
            <div style={{ ...calGrid, gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(7, 1fr)' }}>
              {(calendar || []).map(zi => (
                <button
                  key={zi.data}
                  disabled={!zi.disponibil || zi.sloturiLibere === 0}
                  onClick={() => setData(zi.data)}
                  style={{
                    ...ziBtn,
                    ...(dataAleasa === zi.data ? ziBtnActiv : {}),
                    ...((!zi.disponibil || zi.sloturiLibere === 0) ? ziBtnInactiv : {}),
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#888' }}>{zi.ziSapt}</span>
                  <span style={{ fontWeight: '700', fontSize: '16px' }}>
                    {new Date(zi.data).getDate()}
                  </span>
                  <span style={{ fontSize: '10px', color: zi.sloturiLibere > 0 ? '#059669' : '#dc2626' }}>
                    {!zi.disponibil ? 'Închis' : zi.sloturiLibere > 0 ? `${zi.sloturiLibere} libere` : 'Complet'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Sloturi orare */}
          {dataAleasa && (
            <div>
              <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>
                Sloturi disponibile pentru {new Date(dataAleasa).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}:
              </p>
              {lslo ? <Spinner /> : (
                <div style={sloturiGrid}>
                  {(sloturi?.sloturi || []).map(slot => (
                    <button
                      key={slot.oraStart}
                      disabled={!slot.disponibil}
                      onClick={() => { setSlot(slot); setPas(3); }}
                      style={{
                        ...slotBtn,
                        ...(slotAles?.oraStart === slot.oraStart ? slotActiv : {}),
                        ...(!slot.disponibil ? slotInactiv : {}),
                      }}
                    >
                      {slot.oraStart}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: '16px' }}>
            <Button onClick={() => setPas(1)} variant="ghost">← Înapoi</Button>
          </div>
        </Card>
      )}

      {/* Pas 3: Date personale */}
      {pas === 3 && slotAles && (
        <Card title="Date de contact">
          {/* Rezumat selecție */}
          <div style={rezumatBox}>
            📅 {new Date(dataAleasa).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}
            &nbsp;·&nbsp;🕐 {slotAles.oraStart} – {slotAles.oraSfarsit}
            &nbsp;·&nbsp;🏛 {ghisee?.find(g => g.id === ghiseuId)?.denumire}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={formCol}>
            <div style={fld}>
              <label style={lbl}>Numele complet *</label>
              <input type="text" style={inp} autoComplete="off"
                {...register('cetateanNume', { required: 'Numele este obligatoriu' })} />
              {errors.cetateanNume && <span style={errMsg}>{errors.cetateanNume.message}</span>}
            </div>
            <div style={{ ...s2col, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
              <div style={fld}>
                <label style={lbl}>Email confirmare</label>
                <input type="email" style={inp} autoComplete="off"
                  {...register('cetateanEmail')} />
              </div>
              <div style={fld}>
                <label style={lbl}>Telefon</label>
                <input type="tel" style={inp} autoComplete="off"
                  {...register('cetateanPhone')} />
              </div>
            </div>
            <div style={fld}>
              <label style={lbl}>Motivul programării *</label>
              <input type="text" style={inp}
                placeholder="ex: Depunere dosar impozit casă, Certificat de urbanism..."
                {...register('motiv', { required: 'Motivul este obligatoriu', minLength: { value: 5, message: 'Minim 5 caractere' } })} />
              {errors.motiv && <span style={errMsg}>{errors.motiv.message}</span>}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button type="submit" disabled={mutation.isLoading} style={{ flex: 1 }}>
                {mutation.isLoading ? 'Se confirmă...' : '✓ Confirmă programarea'}
              </Button>
              <Button onClick={() => setPas(2)} variant="ghost">← Înapoi</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Programările mele ────────────────────────────────────
function TabAleMele() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery('programari-mele', progAPI.getMele);

  const anuleazaMut = useMutation(
    (id) => progAPI.anuleaza(id, 'Anulat de cetățean'),
    {
      onSuccess: () => { toast.success('Programare anulată'); qc.invalidateQueries('programari-mele'); },
      onError: () => toast.error('Nu se poate anula'),
    }
  );

  if (isLoading) return <Spinner />;

  const viitoare  = (data || []).filter(p => p.data_programare >= new Date().toISOString().split('T')[0] && p.status === 'confirmata');
  const anterioare = (data || []).filter(p => !viitoare.includes(p));

  return (
    <div>
      {viitoare.length > 0 && (
        <Card title={`Programări viitoare (${viitoare.length})`}>
          {viitoare.map(p => <ProgramareCard key={p.id} p={p} onAnuleaza={id => anuleazaMut.mutate(id)} />)}
        </Card>
      )}
      {anterioare.length > 0 && (
        <Card title="Programări anterioare">
          {anterioare.map(p => <ProgramareCard key={p.id} p={p} />)}
        </Card>
      )}
      {(data || []).length === 0 && (
        <Card><p style={{ color: '#888', textAlign: 'center', padding: '32px' }}>Nu ai programări înregistrate</p></Card>
      )}
    </div>
  );
}

function ProgramareCard({ p, onAnuleaza }) {
  const cfg = STATUS_PROG[p.status] || STATUS_PROG.confirmata;
  const eViitoare = p.data_programare >= new Date().toISOString().split('T')[0] && p.status === 'confirmata';

  return (
    <div style={progCard}>
      <div style={progMain}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ ...progStatus, color: cfg.color, backgroundColor: cfg.bg }}>{cfg.label}</span>
          <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '700', color: '#2d2d7e' }}>
            {p.cod_confirmare}
          </span>
        </div>
        <div style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a2e' }}>
          📅 {new Date(p.data_programare).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          &nbsp;· 🕐 {p.ora_start?.substring(0,5)} – {p.ora_sfarsit?.substring(0,5)}
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
          📍 {p.ghiseu_denumire} {p.ghiseu_locatie && `· ${p.ghiseu_locatie}`}
        </div>
        <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
          {p.motiv}
        </div>
      </div>
      {eViitoare && onAnuleaza && (
        <Button onClick={() => onAnuleaza(p.id)} variant="danger" style={{ padding: '6px 12px', fontSize: '12px' }}>
          Anulează
        </Button>
      )}
    </div>
  );
}

// ── Tab: Agenda funcționar ────────────────────────────────────
function TabAgenda() {
  const qc = useQueryClient();
  const [ghiseuFiltru, setGhiseuFiltru] = useState('');
  const w = useWindowWidth();
  const isMobile = w < 640;
  const { data: ghisee } = useQuery('ghisee', progAPI.getGhisee);
  const { data, isLoading } = useQuery(
    ['agenda-azi', ghiseuFiltru],
    () => progAPI.getAzi(ghiseuFiltru),
    { refetchInterval: 60000 }
  );

  const updateMut = useMutation(
    ({ id, status }) => progAPI.updateStatus(id, { status }),
    {
      onSuccess: () => { toast.success('Status actualizat'); qc.invalidateQueries('agenda-azi'); },
    }
  );

  if (isLoading) return <Spinner />;

  const programari = data?.data || [];
  const confirmateAzi = programari.filter(p => p.status === 'confirmata').length;
  const prezente     = programari.filter(p => p.status === 'prezenta').length;

  return (
    <div>
      <div style={{ ...grid3, gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)' }}>
        <StatCard label="Total azi"   value={programari.length} color="#2d2d7e" />
        <StatCard label="Confirmate"  value={confirmateAzi}     color="#f59e0b" />
        <StatCard label="Prezente"    value={prezente}          color="#059669" />
      </div>

      <Card
        title={`Agenda ${data?.data_azi ? new Date(data.data_azi).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' }) : 'azi'}`}
        action={
          <select style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
            value={ghiseuFiltru} onChange={e => setGhiseuFiltru(e.target.value)}>
            <option value="">Toate ghișeele</option>
            {(ghisee || []).map(g => <option key={g.id} value={g.id}>{g.denumire}</option>)}
          </select>
        }
      >
        {programari.length === 0
          ? <p style={{ color: '#888', textAlign: 'center', padding: '32px' }}>Nicio programare pentru azi</p>
          : programari.map(p => {
            const cfg = STATUS_PROG[p.status] || STATUS_PROG.confirmata;
            return (
              <div key={p.id} style={agendaRow}>
                <div style={agendaOra}>
                  <strong>{p.ora_start?.substring(0,5)}</strong>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>{p.ora_sfarsit?.substring(0,5)}</span>
                </div>
                <div style={agendaMain}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>{p.cetatean_nume}</span>
                    <span style={{ ...progStatus, color: cfg.color, backgroundColor: cfg.bg }}>{cfg.label}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#888' }}>{p.cod_confirmare}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{p.motiv}</div>
                  {p.cetatean_phone && <div style={{ fontSize: '12px', color: '#9ca3af' }}>📞 {p.cetatean_phone}</div>}
                  {p.numar_cerere && <div style={{ fontSize: '12px', color: '#2d2d7e' }}>🔗 Cerere {p.numar_cerere}</div>}
                </div>
                {p.status === 'confirmata' && (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => updateMut.mutate({ id: p.id, status: 'prezenta' })}
                      style={{ ...btnMic, color: '#059669', borderColor: '#059669' }}>✓ Prezent</button>
                    <button onClick={() => updateMut.mutate({ id: p.id, status: 'neprezentata' })}
                      style={{ ...btnMic, color: '#dc2626', borderColor: '#dc2626' }}>✗ Absent</button>
                  </div>
                )}
              </div>
            );
          })
        }
      </Card>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const tabs     = { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #f0f0f0' };
const tabBtn   = { padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#888', borderBottom: '2px solid transparent', marginBottom: '-2px' };
const tabActive = { color: '#2d2d7e', borderBottomColor: '#2d2d7e' };
const progresSteps = { display: 'flex', justifyContent: 'center', gap: '0', marginBottom: '24px' };
const stepItem  = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1, maxWidth: '120px' };
const stepCircle = { width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', backgroundColor: '#e5e7eb', color: '#9ca3af' };
const stepActive = { backgroundColor: '#2d2d7e', color: '#fff' };
const stepDone   = { backgroundColor: '#059669', color: '#fff' };
const stepLabel  = { fontSize: '11px', textAlign: 'center' };
const ghiseeGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' };
const ghiseuBtn  = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '16px', border: '1.5px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', backgroundColor: '#fff', transition: 'all .15s', textAlign: 'center' };
const calGrid    = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' };
const ziBtn      = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 4px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#fff', gap: '2px' };
const ziBtnActiv = { borderColor: '#2d2d7e', backgroundColor: '#eff6ff' };
const ziBtnInactiv = { opacity: 0.4, cursor: 'not-allowed' };
const sloturiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '8px' };
const slotBtn    = { padding: '8px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#fff', fontSize: '13px', fontWeight: '600', color: '#1a1a2e' };
const slotActiv  = { borderColor: '#2d2d7e', backgroundColor: '#eff6ff', color: '#2d2d7e' };
const slotInactiv = { opacity: 0.3, cursor: 'not-allowed', textDecoration: 'line-through' };
const rezumatBox = { padding: '12px 16px', backgroundColor: '#f0f9ff', borderRadius: '8px', fontSize: '14px', color: '#0369a1', marginBottom: '20px', fontWeight: '500' };
const formCol    = { display: 'flex', flexDirection: 'column', gap: '16px' };
const fld        = { display: 'flex', flexDirection: 'column', gap: '4px' };
const lbl        = { fontSize: '13px', fontWeight: '500', color: '#555' };
const inp        = { padding: '10px 12px', fontSize: '16px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', WebkitAppearance: 'none' };
const errMsg     = { fontSize: '12px', color: '#dc2626' };
const s2col      = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };
const succesBox  = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px', textAlign: 'center' };
const codBox     = { fontSize: '32px', fontWeight: '900', letterSpacing: '6px', color: '#2d2d7e', fontFamily: 'monospace', padding: '16px 24px', backgroundColor: '#f0f4ff', borderRadius: '12px', margin: '8px 0' };
const detaliiBox = { width: '100%', maxWidth: '400px', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', marginTop: '16px' };
const detaliiRow = { display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #f5f5f5', fontSize: '14px' };
const progCard   = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 0', borderBottom: '1px solid #f5f5f5', gap: '12px' };
const progMain   = { flex: 1 };
const progStatus = { display: 'inline-block', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px' };
const agendaRow  = { display: 'flex', gap: '16px', padding: '14px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' };
const agendaOra  = { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', flexShrink: 0 };
const agendaMain = { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' };
const btnMic     = { padding: '5px 10px', fontSize: '12px', border: '1px solid', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff' };
const grid3      = { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', marginBottom: '20px' };
